import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  ExternalLink,
  FileCheck2,
  GraduationCap,
  HeartHandshake,
  Languages,
  MessageSquareText,
  Plane,
  ShieldCheck,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import { PROFESSIONALS_IMAGE, STUDY_IMAGE } from "../lib/brand";

const officialLinks = {
  pr: "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada.html",
  student: "https://www.canada.ca/en/immigration-refugees-citizenship/services/study-canada/study-permit.html",
  tcf: "https://www.france-education-international.fr/test/tcf-canada",
  work: "https://www.canada.ca/en/immigration-refugees-citizenship/services/work-canada/permit.html",
  sponsorship: "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/family-sponsorship.html",
  visitor: "https://www.canada.ca/en/immigration-refugees-citizenship/services/visit-canada/visitor-visa.html",
};

const services = {
  fr: [
    {
      k: "pr",
      id: "pr",
      icon: BriefcaseBusiness,
      title: "Résidence permanente",
      desc: "Explorer les voies adaptées aux travailleurs qualifiés et organiser une préparation réaliste du profil.",
      start: "Point de départ fréquent : diplôme postsecondaire, expérience professionnelle qualifiée et test de langue. Les critères exacts dépendent du programme.",
      details: [
        "Lecture générale du profil : études, expérience, langues, âge et situation familiale",
        "Compréhension d’Entrée Express et des programmes provinciaux",
        "Estimation du score CRS et scénarios d’amélioration",
        "Organisation des preuves d’emploi et des documents scolaires",
        "Plan de préparation avec priorités et échéances",
        "Vérification finale des exigences sur les sources officielles",
      ],
    },
    {
      k: "student",
      id: "student",
      icon: GraduationCap,
      title: "Études au Canada",
      desc: "Transformer un projet d’études en parcours cohérent : programme, admission, financement et permis.",
      start: "Point de départ fréquent : diplôme d’enseignement secondaire pour de nombreux programmes collégiaux ou universitaires. Chaque établissement fixe ses propres critères.",
      details: [
        "Recherche d’établissements et de programmes cohérents avec le parcours",
        "Vérification de l’admissibilité générale et des exigences linguistiques",
        "Planification des frais de scolarité et des ressources financières",
        "Compréhension de la lettre d’acceptation et de la PAL/TAL lorsqu’elle est requise",
        "Organisation du plan d’études et des pièces justificatives",
        "Compréhension des conditions liées au permis d’études",
      ],
    },
    {
      k: "tcf",
      id: "tcf",
      icon: Languages,
      title: "TCF Canada",
      desc: "Construire une préparation structurée pour atteindre un niveau de français utile au projet canadien.",
      start: "Aucun diplôme particulier n’est requis pour passer le TCF Canada. Le niveau cible dépend du programme d’immigration ou de citoyenneté visé.",
      details: [
        "Diagnostic du niveau actuel",
        "Compréhension du format et des compétences évaluées",
        "Définition d’un score cible selon le projet",
        "Plan de travail hebdomadaire et exercices progressifs",
        "Techniques de gestion du temps et des consignes",
        "Suivi de la progression avant l’inscription à l’examen",
      ],
    },
    {
      k: "work",
      id: "work",
      icon: FileCheck2,
      title: "Permis de travail",
      desc: "Comprendre les différentes catégories de permis et les conditions qui s’appliquent à la situation.",
      start: "Le point de départ varie : offre d’emploi, admissibilité à un permis ouvert, programme particulier ou statut d’un membre de la famille.",
      details: [
        "Différence entre permis fermé et permis ouvert",
        "Rôle possible d’une offre d’emploi et d’une EIMT/LMIA",
        "Lecture des critères généraux et propres à la catégorie",
        "Organisation des preuves de qualification et d’expérience",
        "Préparation de la liste documentaire",
        "Orientation vers les pages officielles correspondant à la situation",
      ],
    },
    {
      k: "sponsorship",
      id: "sponsorship",
      icon: HeartHandshake,
      title: "Parrainage familial",
      desc: "Structurer les preuves et la chronologie d’un projet de couple ou de famille.",
      start: "Point de départ : relation admissible et répondant aux critères du programme, avec des preuves cohérentes de la relation et de la situation familiale.",
      details: [
        "Mariage, union de fait et catégories familiales",
        "Organisation des preuves de relation",
        "Chronologie des photos, communications et documents communs",
        "Compréhension des demandes à l’intérieur ou à l’extérieur du Canada",
        "Préparation des informations sur les enfants à charge",
        "Liste de contrôle et suivi des documents manquants",
      ],
    },
    {
      k: "visitor",
      id: "visitor",
      icon: Plane,
      title: "Visa visiteur",
      desc: "Présenter un projet de visite cohérent et organiser les pièces qui soutiennent la demande.",
      start: "Point de départ : objectif temporaire clair, capacité financière suffisante et preuves crédibles de la situation personnelle.",
      details: [
        "Clarification du motif et de la durée du voyage",
        "Organisation des preuves financières",
        "Présentation des attaches personnelles et professionnelles",
        "Préparation de la lettre explicative ou d’invitation",
        "Vérification de la cohérence des formulaires et documents",
        "Lecture des motifs de refus et pistes d’amélioration documentaire",
      ],
    },
    {
      k: "orientation",
      id: "orientation",
      icon: MessageSquareText,
      title: "Orientation personnalisée",
      desc: "Faire le point sur le projet, comprendre les options à explorer et repartir avec une feuille de route.",
      start: "Préparez votre parcours scolaire, votre expérience, vos résultats linguistiques, votre budget indicatif et vos principales questions.",
      details: [
        "Échange structuré sur le profil et l’objectif",
        "Identification des pistes à approfondir",
        "Priorisation des prochaines actions",
        "Liste de sources officielles à consulter",
        "Organisation des documents déjà disponibles",
        "Aucune représentation auprès d’IRCC et aucun avis juridique",
      ],
    },
  ],
  en: [
    {
      k: "pr",
      id: "pr",
      icon: BriefcaseBusiness,
      title: "Permanent residence",
      desc: "Explore pathways for skilled workers and organize a realistic profile preparation plan.",
      start: "A common starting point is post-secondary education, qualifying work experience and a language test. Exact criteria depend on the program.",
      details: [
        "General profile review: education, experience, languages, age and family situation",
        "Understanding Express Entry and provincial programs",
        "CRS score estimate and improvement scenarios",
        "Organization of employment and education evidence",
        "Preparation roadmap with priorities and timelines",
        "Final verification of requirements using official sources",
      ],
    },
    {
      k: "student",
      id: "student",
      icon: GraduationCap,
      title: "Study in Canada",
      desc: "Turn a study idea into a coherent plan: program, admission, funding and permit preparation.",
      start: "A secondary-school credential is a common starting point for many college or university programs. Each institution sets its own admission rules.",
      details: [
        "Search for institutions and programs aligned with your background",
        "Review of general admission and language requirements",
        "Planning for tuition and financial resources",
        "Understanding the letter of acceptance and PAL/TAL when required",
        "Organization of the study plan and supporting documents",
        "Understanding conditions attached to a study permit",
      ],
    },
    {
      k: "tcf",
      id: "tcf",
      icon: Languages,
      title: "TCF Canada",
      desc: "Build a structured preparation plan for the French level needed by your Canadian project.",
      start: "No specific diploma is required to take TCF Canada. The target level depends on the immigration or citizenship program.",
      details: [
        "Assessment of your current level",
        "Understanding the test format and evaluated skills",
        "Setting a target score based on your project",
        "Weekly study plan and progressive exercises",
        "Time-management and instruction-reading techniques",
        "Progress follow-up before registering for the test",
      ],
    },
    {
      k: "work",
      id: "work",
      icon: FileCheck2,
      title: "Work permits",
      desc: "Understand permit categories and the conditions that may apply to your situation.",
      start: "The starting point varies: a job offer, eligibility for an open permit, a specific program or a family member’s status.",
      details: [
        "Difference between employer-specific and open work permits",
        "Possible role of a job offer and LMIA",
        "Review of general and category-specific criteria",
        "Organization of qualification and experience evidence",
        "Preparation of a document checklist",
        "Direction to official pages matching the situation",
      ],
    },
    {
      k: "sponsorship",
      id: "sponsorship",
      icon: HeartHandshake,
      title: "Family sponsorship",
      desc: "Structure the evidence and timeline for a couple or family project.",
      start: "The starting point is an eligible relationship that meets program rules, supported by consistent evidence of the relationship and family situation.",
      details: [
        "Marriage, common-law and family categories",
        "Organization of relationship evidence",
        "Timeline of photos, communication and joint documents",
        "Understanding inland and outland applications",
        "Preparation of information about dependent children",
        "Checklist and follow-up of missing documents",
      ],
    },
    {
      k: "visitor",
      id: "visitor",
      icon: Plane,
      title: "Visitor visa",
      desc: "Present a coherent temporary visit and organize the evidence supporting the application.",
      start: "The starting point is a clear temporary purpose, sufficient financial capacity and credible evidence of your personal situation.",
      details: [
        "Clarifying the purpose and duration of the trip",
        "Organizing financial evidence",
        "Presenting personal and professional ties",
        "Preparing an explanation or invitation letter",
        "Checking consistency across forms and documents",
        "Reviewing refusal reasons and documentary improvement areas",
      ],
    },
    {
      k: "orientation",
      id: "orientation",
      icon: MessageSquareText,
      title: "Personalized orientation",
      desc: "Review the project, understand options worth exploring and leave with a roadmap.",
      start: "Prepare your education, experience, language results, indicative budget and main questions.",
      details: [
        "Structured conversation about your profile and goal",
        "Identification of pathways to investigate further",
        "Prioritization of next actions",
        "List of official sources to consult",
        "Organization of documents already available",
        "No representation before IRCC and no legal advice",
      ],
    },
  ],
};

export default function Services() {
  const { lang } = useLang();
  const list = services[lang] || services.fr;

  return (
    <div className="bg-alabaster" data-testid="services-page">
      <PageHero
        eyebrow={lang === "fr" ? "Services JNIC" : "JNIC services"}
        title={lang === "fr" ? "Sept façons d’avancer avec plus de clarté" : "Seven ways to move forward with more clarity"}
        subtitle={
          lang === "fr"
            ? "Information générale, préparation pédagogique et organisation du projet pour les francophones partout dans le monde."
            : "General information, educational preparation and project organization for French speakers worldwide."
        }
        image={PROFESSIONALS_IMAGE}
        imageAlt={lang === "fr" ? "Équipe internationale travaillant ensemble" : "International team working together"}
      >
        <div className="flex flex-wrap gap-3 text-xs text-white/70">
          {list.slice(0, 5).map((service) => (
            <a key={service.id} href={`#${service.id}`} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 hover:border-gold/50 transition">
              {service.title}
            </a>
          ))}
        </div>
      </PageHero>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 space-y-6">
        {list.map((service, index) => {
          const Icon = service.icon;
          const source = officialLinks[service.k];
          return (
            <motion.article
              key={service.k}
              id={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              className="relative scroll-mt-28 overflow-hidden bg-white rounded-[2rem] border border-navy/8 shadow-[0_18px_55px_rgba(6,22,42,.055)]"
              data-testid={`service-detail-${service.k}`}
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${index % 2 === 0 ? "bg-redcanada" : "bg-gold"}`} />
              <div className="grid lg:grid-cols-12 gap-8 p-8 lg:p-12">
                <div className="lg:col-span-4">
                  <div className="flex items-center justify-between gap-4">
                    <p className={`text-[11px] tracking-[0.3em] uppercase ${index % 2 === 0 ? "text-redcanada" : "text-gold"}`}>0{index + 1}</p>
                    <span className="w-11 h-11 rounded-2xl bg-navy text-gold grid place-items-center"><Icon size={21} /></span>
                  </div>
                  <h2 className="font-serif text-4xl lg:text-5xl text-navy leading-[0.98] mt-5">{service.title}</h2>
                  <p className="mt-5 text-navy/65 leading-relaxed">{service.desc}</p>
                  <div className="mt-6 rounded-2xl bg-cream/75 border border-navy/6 p-5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-redcanada font-semibold">
                      {lang === "fr" ? "Point de départ habituel" : "Typical starting point"}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-navy/70">{service.start}</p>
                  </div>
                  <Link
                    to="/booking"
                    className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-redcanada transition"
                    data-testid={`service-book-${service.k}`}
                  >
                    {lang === "fr" ? "Réserver un appel" : "Book a call"} <ArrowRight size={15} />
                  </Link>
                </div>

                <div className="lg:col-span-8 lg:pl-10 lg:border-l border-navy/10">
                  <p className="text-xs tracking-[0.2em] uppercase text-navy/45 mb-7">
                    {lang === "fr" ? "Ce qui est couvert" : "What is covered"}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-7 gap-y-4">
                    {service.details.map((detail) => (
                      <div key={detail} className="flex items-start gap-3 text-navy/75 text-sm leading-relaxed">
                        <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-cream grid place-items-center">
                          <Check size={14} className={index % 2 === 0 ? "text-redcanada" : "text-gold"} />
                        </span>
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>

                  {source && (
                    <a
                      href={source}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-redcanada transition"
                    >
                      {lang === "fr" ? "Consulter la source officielle" : "Consult the official source"} <ExternalLink size={15} />
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          );
        })}
      </section>

      {/* Section: Modalités de Paiement des Honoraires & Garantie de Remboursement */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-white rounded-[2rem] border border-navy/10 p-8 lg:p-12 shadow-[0_16px_50px_rgba(6,22,42,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-xl bg-redcanada/10 text-redcanada flex items-center justify-center font-bold text-lg">
              %
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-redcanada">
                {lang === "fr" ? "Modalités de règlement claires" : "Clear payment terms"}
              </p>
              <h3 className="font-serif text-2xl lg:text-3xl font-bold text-navy">
                {lang === "fr" ? "Échelonnement des honoraires & Garantie de remboursement" : "Fee installment schedule & Refund guarantee"}
              </h3>
            </div>
          </div>

          <p className="text-navy/70 text-sm max-w-3xl mb-8 leading-relaxed">
            {lang === "fr"
              ? "Chez JNIC, les honoraires d'accompagnement sont alignés sur la progression et le succès de votre dossier selon l'échéancier strict suivant :"
              : "At JNIC, consulting fees are tied to your case progress and success under the following strict installment schedule:"}
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="p-6 rounded-2xl bg-cream/70 border border-navy/8">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-navy/50">
                  {lang === "fr" ? "Étape 1" : "Step 1"}
                </span>
                <span className="text-2xl font-bold text-redcanada font-serif">25%</span>
              </div>
              <h4 className="font-serif text-lg font-bold text-navy mb-2">
                {lang === "fr" ? "Au début de la procédure" : "At procedure launch"}
              </h4>
              <p className="text-xs text-navy/65 leading-relaxed">
                {lang === "fr"
                  ? "Ouverture du dossier, audit documentaire initial, diagnostic d'admissibilité et stratégie d'immigration."
                  : "File opening, initial document audit, eligibility diagnostic, and immigration strategy."}
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-cream/70 border border-navy/8">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-navy/50">
                  {lang === "fr" ? "Étape 2" : "Step 2"}
                </span>
                <span className="text-2xl font-bold text-redcanada font-serif">25%</span>
              </div>
              <h4 className="font-serif text-lg font-bold text-navy mb-2">
                {lang === "fr" ? "À l'ITA (Invitation à postuler)" : "At ITA (Invitation to Apply)"}
              </h4>
              <p className="text-xs text-navy/65 leading-relaxed">
                {lang === "fr"
                  ? "Dès la réception de l'invitation officielle d'IRCC pour finaliser et soumettre la demande de résidence permanente."
                  : "Upon receipt of the official IRCC invitation to finalize and submit the permanent residence application."}
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-cream/70 border border-navy/8">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-navy/50">
                  {lang === "fr" ? "Étape 3" : "Step 3"}
                </span>
                <span className="text-2xl font-bold text-redcanada font-serif">50%</span>
              </div>
              <h4 className="font-serif text-lg font-bold text-navy mb-2">
                {lang === "fr" ? "À l'émission du Visa IRCC" : "Upon IRCC Visa Issuance"}
              </h4>
              <p className="text-xs text-navy/65 leading-relaxed">
                {lang === "fr"
                  ? "Le solde restant n'est exigible qu'une fois votre visa de résident permanent effectivement délivré par IRCC."
                  : "The remaining balance is only due once your permanent resident visa has been successfully issued by IRCC."}
              </p>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-red-50/80 border border-red-200/80 flex items-start gap-4">
            <span className="w-8 h-8 rounded-full bg-redcanada text-white flex items-center justify-center shrink-0 font-bold text-sm mt-0.5">
              🛡️
            </span>
            <div className="text-xs leading-relaxed text-[#06162A]">
              <strong className="text-redcanada font-bold block mb-1">
                {lang === "fr" ? "Garantie contractuelle de remboursement en cas de refus :" : "Contractual refund guarantee in case of refusal:"}
              </strong>
              {lang === "fr"
                ? "En cas de refus de la demande par IRCC, 50% du montant déjà versé est intégralement remboursé au client, vu que les 50% restant dus après l'émission du visa n'auront pas encore été versés."
                : "In the event of a refusal by IRCC, 50% of the amount already paid is fully refunded to the client, given that the remaining 50% due after visa issuance has not yet been paid."}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-navy text-white">
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-5 min-h-[330px] relative">
              <img src={STUDY_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-navy/15 to-navy/75" />
            </div>
            <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16">
              <div className="w-12 h-12 rounded-2xl bg-white/8 text-gold grid place-items-center mb-6"><ShieldCheck size={23} /></div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-gold">
                {lang === "fr" ? "Transparence" : "Transparency"}
              </p>
              <h3 className="font-serif text-4xl lg:text-5xl tracking-tight mt-4">
                {lang === "fr" ? "L’information officielle reste la référence." : "Official information remains the reference."}
              </h3>
              <p className="text-white/65 max-w-3xl mt-5 leading-relaxed">
                {lang === "fr"
                  ? "JNIC n’est pas un cabinet juridique et ne représente pas les candidats auprès d’IRCC. L’objectif est de rendre l’information compréhensible, d’aider à organiser le projet et de diriger chaque personne vers les sources officielles pertinentes."
                  : "JNIC is not a law firm and does not represent applicants before IRCC. The goal is to make information understandable, help organize the project and direct each person to the relevant official sources."}
              </p>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-redcanada px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-navy transition">
                {lang === "fr" ? "Poser une question" : "Ask a question"} <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
